<template>
  <ElFormItem :prop="surfaceArrayProp" class="full-width surfaces-container">
    <!-- Surface Composition Section -->
    <div class="surface-header d--f mb-15px">
      <label class="fw-500 font-16px">{{ title }} <span class="text-red">*</span></label>
      <div class="flex-spacer"></div>
      <AppButton v-if="surfaces.length < 4" variant="primary" size="small" @click="addSurface" class="add-surface-btn">
        <AppIcon color="white" type="plus" size="14" class="mr-5px" /> Add surface
      </AppButton>
    </div>

    <p class="text-muted font-14px mb-5px">{{ description }}</p>
    <p class="text-muted font-14px">
      Select each surface that applies. Add up to 4 surface types. The total must equal 100%.
    </p>

    <div class="surface-container">
      <div v-for="(surface, index) in surfaces" :key="index" class="surface-card">
        <div class="surface-card-header d--f jc--sb ai--c">
          <span class="surface-number">Surface {{ index + 1 }}</span>
          <AppButton
            v-if="surfaces.length > 1"
            variant="outlined"
            @click="() => removeSurface(index)"
            class="remove-btn"
            size="small"
          >
            <AppIcon color="primary" type="trash" size="16" />
          </AppButton>
        </div>

        <div class="surface-card-body">
          <div class="mb-15px">
            <label class="d--b mb-5px font-14px">Surface type</label>
            <AppSelect
              v-model="surface.type"
              :options="surfaceTypeOptions"
              placeholder="Select surface type"
              class="w--100"
              size="large"
              @change="() => updateSurfaceCoefficient(index)"
              required
            />
          </div>

          <div class="mb-10px">
            <label class="d--b mb-5px font-14px">Percentage (%)</label>
            <AppInput
              v-model="surface.percentage"
              type="number"
              placeholder="Enter percentage"
              class="w--100"
              min="0"
              max="100"
              required
            />
          </div>

          <div class="mb-10px">
            <label class="d--b mb-5px font-14px">Runoff coefficient</label>
            <AppInput
              v-model="surface.coefficient"
              type="number"
              placeholder="Enter runoff coefficient"
              class="w--100"
              min="0"
              max="1"
              step="0.01"
              required
            />
          </div>
        </div>
      </div>
    </div>

    <div class="percentage-summary-wrapper">
      <div class="percentage-block">
        <div class="percentage-title">Total percentage</div>
        <div
          class="percentage-number"
          :class="{
            'percentage-success': totalPercentage === 100,
            'percentage-warning': totalPercentage < 100,
            'percentage-danger': totalPercentage > 100,
          }"
        >
          {{ totalPercentage }}%
        </div>

        <div v-if="percentageError" class="percentage-error-message">
          {{ percentageError }}
        </div>
      </div>
    </div>

    <div class="coefficient-summary-wrapper">
      <div class="coefficient-row">
        <span class="coefficient-label">Average runoff coefficient:</span>
        <span class="coefficient-number">{{ averageCoefficient.toFixed(2) }}</span>
      </div>
    </div>

    <!-- Stormwater Target Section (only for post-development) -->
    <template v-if="showStormwaterTarget">
      <AppLabel for="stormwater_target_m3" class="fw-500 font-16px mb-0 mt-30px">
        Stormwater Retention/Detention Target
      </AppLabel>

      <p class="text-muted font-14px mb-15px">
        (Optional - if not provided, target will be calculated based on runoff increase)<br /><br />
        This could be based on:<br />
        • Municipal stormwater management requirements<br />
        • Site-specific engineering analysis<br />
        • Regulatory permit conditions
      </p>

      <ElFormItem prop="stormwater_target_m3" class="full-width">
        <AppInput
          id="stormwater_target_m3"
          v-model="store.formData.stormwater_target_m3"
          type="number"
          placeholder="Enter target volume in cubic meters (m³)"
          class="full-width"
          min="0"
          step="0.1"
        >
          <template #suffix>m³</template>
        </AppInput>
      </ElFormItem>

      <p class="text-muted font-12px">
        If provided, this target will be used instead of the calculated runoff difference between pre and
        post-development. Typical values range from 5-50 m³ per 1000 m² of developed area, depending on local
        requirements.
      </p>
    </template>
  </ElFormItem>
</template>

<script setup>
import { computed } from "vue";
import { ElFormItem } from "element-plus";
import { AppButton, AppIcon, AppInput, AppLabel, AppSelect } from "@/components/ui";
import { useReportFormStore } from "@/stores/reportFormStore";
import { getSurfaceTypeOptions } from "@/utilities/collections/newReportOptions";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  surfaceType: {
    type: String,
    required: true,
    validator: (value) => ["pre_development_surfaces", "post_development_surfaces"].includes(value),
  },
  showStormwaterTarget: {
    type: Boolean,
    default: false,
  },
});

const store = useReportFormStore();

// Computed properties based on surface type
const surfaces = computed(() => store.formData[props.surfaceType]);
const surfaceArrayProp = computed(() => props.surfaceType);

const totalPercentage = computed(() => {
  if (props.surfaceType === "pre_development_surfaces") {
    return store.totalSurfacePercentage;
  } else {
    return store.totalPostDevelopmentSurfacePercentage;
  }
});

const averageCoefficient = computed(() => {
  if (props.surfaceType === "pre_development_surfaces") {
    return store.averageRunoffCoefficient;
  } else {
    return store.averagePostDevelopmentRunoffCoefficient;
  }
});

// Get filtered surface options based on project type
const surfaceTypeOptions = computed(() => {
  return getSurfaceTypeOptions(store.formData.project_type);
});

// Compute error message locally based on store data
const percentageError = computed(() => {
  if (totalPercentage.value > 100) {
    return "Total percentage exceeds 100%";
  } else if (surfaces.value.length > 0 && totalPercentage.value < 100) {
    return "Total percentage must equal 100%";
  }
  return "";
});

// Methods that call the appropriate store methods based on surface type
const addSurface = () => {
  if (props.surfaceType === "pre_development_surfaces") {
    store.addSurface();
  } else {
    store.addPostDevelopmentSurface();
  }
};

const removeSurface = (index) => {
  if (props.surfaceType === "pre_development_surfaces") {
    store.removeSurface(index);
  } else {
    store.removePostDevelopmentSurface(index);
  }
};

const updateSurfaceCoefficient = (index) => {
  if (props.surfaceType === "pre_development_surfaces") {
    store.updateSurfaceCoefficient(index);
  } else {
    store.updatePostDevelopmentSurfaceCoefficient(index);
  }
};
</script>

<style lang="scss" scoped>
.text-red {
  color: $color-red;
}

.surface-container {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px 0px -10px;
  width: calc(100% + 20px);
}

.surface-card {
  flex: 0 0 calc(50% - 20px);
  margin: 20px 10px 0px 10px;
  background-color: white;
  border: 1px solid $color-grey-10;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.surface-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.surface-card-header {
  background-color: $color-grey-12;
  padding: 10px 15px;
  border-bottom: 1px solid $color-grey-10;
}

.surface-number {
  font-weight: 500;
  color: $color-grey-4;
}

.surface-card-body {
  padding: 15px;
}

.add-surface-btn {
  padding: 6px 12px;
  font-size: 14px;
}

.font-14px {
  font-size: 14px;
}

.font-16px {
  font-size: 16px;
}

.font-12px {
  font-size: 12px;
}

.surface-header {
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.flex-spacer {
  flex-grow: 1;
}

.percentage-summary-wrapper {
  width: 100%;
  background-color: white;
  border: 1px solid $color-grey-10;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.percentage-block {
  display: block;
  width: 100%;
  text-align: center;
}

.percentage-title {
  display: block;
  width: 100%;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: $color-grey-4;
}

.percentage-number {
  display: block;
  width: 100%;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.2;
}

.percentage-success {
  color: $color-green;
}

.percentage-warning {
  color: $color-orange;
}

.percentage-danger {
  color: $color-red;
}

.percentage-error-message {
  display: block;
  width: 100%;
  color: $color-red;
  font-size: 14px;
  margin-top: 10px;
  font-style: italic;
  text-align: center;
}

.coefficient-summary-wrapper {
  width: 100%;
  background-color: white;
  border: 1px solid $color-grey-10;
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.coefficient-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.coefficient-label {
  font-size: 16px;
  color: $color-grey-4;
}

.coefficient-number {
  font-size: 18px;
  font-weight: 500;
  color: $color-primary;
}

.surfaces-container {
  max-width: 100%;
  overflow: hidden;
}

.mt-30px {
  margin-top: 30px;
}

@media (max-width: 767px) {
  .surface-card {
    flex: 0 0 calc(100% - 20px);
  }
}
</style>
